/**
 * projectPipelineConfig.ts — the project model, FRONTEND MIRROR (BSG Projects, 2026-09-24).
 *
 * Canonical source: `backend/src/config/projectPipeline.ts`. Everything from the first
 * `export type` down is a VERBATIM copy of that file — `backend/src/__tests__/
 * projectPipelineMirror.test.ts` compares the two byte for byte and fails on any drift.
 * Change the backend file, then copy the body here.
 *
 * Spec: `refactor/design-sync/bsg-projects/README.md` §2–§4.
 */

export type ProjectTypeKey = 'full' | 'documentation' | 'doc_impl' | 'custom' | 'implementation' | 'training';
export type ProjectPhaseId = 'documentation' | 'implementation' | 'training' | 'custom';
export type ProjectOffId = 'future' | 'canceled' | 'invalid' | 'done';
export type ProjectBookingKind = 'doc' | 'delivery' | 'training';

export interface ProjectTypeDef {
  key: ProjectTypeKey;
  label: string;
  /** The chip / tab short form. */
  short: string;
  phases: ProjectPhaseId[];
  /** The two single-phase types kept from the old sprint types. */
  kept?: boolean;
}

export interface ProjectStageDef {
  key: string;
  label: string;
  /** The descriptor line — the small grey line under the stage. Verbatim. */
  desc: string;
  phase: ProjectPhaseId;
  /** A side stage: beside its phase, entered by hand, returns to `from_stage` on leaving. */
  side?: boolean;
  /** Which call's booking chip the stage carries. */
  booking?: ProjectBookingKind;
  /** The trigger that WILL move a project here once project automations are on (AUTO · OFF). */
  auto?: string;
  /** The trigger that moves a project here TODAY (AUTO · LIVE). Only one exists. */
  live?: string;
  /** An exit gate: the field required to leave the stage (soft — "move anyway" is logged). */
  gate?: 'deliver_by';
  /** Carries the DeliveryMethodChoice (Email · Next call). */
  choice?: boolean;
  /** The types this stage closes: reaching it on one of them means the project is Done. */
  closes?: ProjectTypeKey[];
  /** Training Call N Complete: N. */
  call_index?: number;
}

export interface ProjectPhaseDef {
  id: ProjectPhaseId;
  label: string;
  color: string;
  /** The forward line. For Training this is the 4-call maximum; use `phaseStages()`. */
  stages: ProjectStageDef[];
  side: ProjectStageDef | null;
}

export interface ProjectOffDef {
  id: ProjectOffId;
  label: string;
  color: string;
  note: string;
}

// ── Types ─────────────────────────────────────────────────────────────────
export const PROJECT_TYPES: ProjectTypeDef[] = [
  { key: 'full', label: 'Full', short: 'Full', phases: ['documentation', 'implementation', 'training'] },
  { key: 'documentation', label: 'Documentation', short: 'Documentation', phases: ['documentation'] },
  { key: 'doc_impl', label: 'Documentation + Implementation', short: 'Doc + Impl', phases: ['documentation', 'implementation'] },
  { key: 'custom', label: 'One-off / Custom', short: 'Custom', phases: ['custom'] },
  { key: 'implementation', label: 'Implementation only', short: 'Impl only', phases: ['implementation'], kept: true },
  { key: 'training', label: 'Training only', short: 'Training only', phases: ['training'], kept: true },
];

export const PROJECT_TYPE_KEYS: ProjectTypeKey[] = PROJECT_TYPES.map((t) => t.key);

export const MIN_TRAINING_CALLS = 1;
export const MAX_TRAINING_CALLS = 4;

/** Training grows with the call count: 1 call → Scheduling → Completed; 4 calls → + Call 1…3 Complete. */
export function trainingStages(calls: number | null | undefined): ProjectStageDef[] {
  const c = clampTrainingCalls(calls);
  const out: ProjectStageDef[] = [
    { key: 'tr_sched', label: 'Scheduling Training Calls', desc: '1 to 4 calls.', phase: 'training', booking: 'training' },
  ];
  for (let i = 1; i < c; i++) {
    out.push({
      key: `tr_call_${i}`,
      label: `Training Call ${i} Complete`,
      desc: `An hour after training call ${i}.`,
      phase: 'training',
      auto: `an hour after training call ${i}`,
      call_index: i,
    });
  }
  out.push({
    key: 'tr_done',
    label: 'Training Completed',
    desc: 'After the last call.',
    phase: 'training',
    auto: 'an hour after the last training call',
    closes: ['full', 'training'],
  });
  return out;
}

export function clampTrainingCalls(calls: number | null | undefined): number {
  const n = Math.round(Number(calls) || MIN_TRAINING_CALLS);
  return Math.max(MIN_TRAINING_CALLS, Math.min(MAX_TRAINING_CALLS, n));
}

// ── Phases + stages ───────────────────────────────────────────────────────
export const PROJECT_PHASES: ProjectPhaseDef[] = [
  {
    id: 'documentation', label: 'Documentation', color: '#3D9CFF',
    stages: [
      { key: 'doc_sched', label: 'Scheduling Doc Call', desc: 'Outcome: a documentation-call date is on the project.', phase: 'documentation', booking: 'doc' },
      { key: 'doc_done', label: 'Doc Call Completed', desc: 'An hour after the documentation call.', phase: 'documentation', auto: 'an hour after the documentation call' },
      { key: 'wf_approved', label: 'Workflow Built & Approved', desc: 'The workflow is published and the client has approved it.', phase: 'documentation', live: 'the client approves the workflow in their portal', closes: ['documentation'] },
    ],
    side: { key: 'wf_refining', label: 'Refining Workflow', desc: 'The client asked to take it back.', phase: 'documentation', side: true },
  },
  {
    id: 'implementation', label: 'Implementation', color: '#0443EF',
    stages: [
      { key: 'impl_new', label: 'New Project Assigned', desc: 'New work for the team — ready to pick up.', phase: 'implementation' },
      { key: 'impl_build', label: 'Constructing Project', desc: 'Build blocks set the deliver-by date; it’s needed to move on.', phase: 'implementation', gate: 'deliver_by' },
      { key: 'impl_ready', label: 'Project Ready for Delivery', desc: 'On the deliver-by date, or moved by hand.', phase: 'implementation', auto: 'the deliver-by date' },
      { key: 'impl_sched', label: 'Scheduling Project Delivery Call', desc: 'Books from one business day after Deliver by. Complete once the call is booked and its date arrives.', phase: 'implementation', booking: 'delivery' },
      { key: 'impl_call_done', label: 'Project Delivery Call Complete', desc: 'An hour after the delivery call.', phase: 'implementation', auto: 'an hour after the delivery call' },
      { key: 'impl_delivered', label: 'Project Delivered', desc: 'Delivered to the client.', phase: 'implementation', closes: ['doc_impl', 'implementation'] },
    ],
    side: { key: 'impl_refining', label: 'Refining Project', desc: 'Revisions requested.', phase: 'implementation', side: true },
  },
  {
    id: 'training', label: 'Training', color: '#5B7C99',
    stages: trainingStages(MAX_TRAINING_CALLS),
    side: null,
  },
  {
    id: 'custom', label: 'Custom', color: '#7D9AB8',
    stages: [
      { key: 'cu_new', label: 'New Custom Project Requested', desc: 'Entered by hand, or from the scope / roadmap.', phase: 'custom' },
      { key: 'cu_build', label: 'Constructing Custom Project', desc: 'Being built by the implementer.', phase: 'custom' },
      { key: 'cu_ready', label: 'Custom Project Ready for Delivery', desc: 'Delivered by email or prepped for the next call — the implementer’s choice.', phase: 'custom', choice: true },
      { key: 'cu_delivered', label: 'Custom Project Delivered', desc: 'Delivered to the client.', phase: 'custom', closes: ['custom'] },
    ],
    side: { key: 'cu_refining', label: 'Refining Custom Project', desc: 'Revisions.', phase: 'custom', side: true },
  },
];

/** Off the board — like sales' Nurture / Lost / Invalid. Done holds every completed project (stamped at its type's close stage). */
export const PROJECT_OFF: ProjectOffDef[] = [
  { id: 'future', label: 'Future Project', color: '#94A3B8', note: 'Ideas and later work — nothing is scheduled.' },
  { id: 'canceled', label: 'Canceled', color: '#5F6670', note: 'Stopped, with a one-line reason.' },
  { id: 'invalid', label: 'Invalid', color: '#7c4dff', note: 'Duplicates and test cards.' },
  { id: 'done', label: 'Done', color: '#2e7d32', note: 'Reached the close stage of its type.' },
];

/** The stage that closes each type — a project OF that type (a real one, not a suggestion) reaching it is Done. */
export const CLOSE_STAGE: Record<ProjectTypeKey, string> = {
  full: 'tr_done',
  documentation: 'wf_approved',
  doc_impl: 'impl_delivered',
  custom: 'cu_delivered',
  implementation: 'impl_delivered',
  training: 'tr_done',
};

/** The one move that is live today: the client's portal approval → Workflow Built & Approved. */
export const PORTAL_APPROVAL_STAGE = 'wf_approved';

// ── The legacy map (README §4) ────────────────────────────────────────────
export interface LegacyStageMapping {
  /** The legacy label, for the type-review list's struck-through "was". */
  label: string;
  /** The new stage, 'done', or null → Unsorted. */
  to: string | null;
  /** The old stage said a call was booked (Doc Call Scheduled / Delivery Call Scheduled). */
  booked?: boolean;
  /** Workflow Sent: the project waits on the client — the workflow status reads "Awaiting approval". */
  awaiting_approval?: boolean;
}

export const LEGACY_PROJECT_STAGE_MAP: Record<string, LegacyStageMapping> = {
  new_sprint: { label: 'New Sprint', to: 'doc_sched' },
  doc_scheduled: { label: 'Doc Call Scheduled', to: 'doc_sched', booked: true },
  doc_completed: { label: 'Doc Call Completed', to: 'doc_done' },
  workflow_sent: { label: 'Workflow Sent', to: 'doc_done', awaiting_approval: true },
  workflow_approved: { label: 'Workflow Approved', to: 'wf_approved' },
  building: { label: 'Building Process', to: 'impl_build' },
  process_built: { label: 'Process Built', to: 'impl_ready' },
  delivery_scheduled: { label: 'Delivery Call Scheduled', to: 'impl_sched', booked: true },
  delivery_complete: { label: 'Delivery Complete', to: 'impl_call_done' },
  scheduling_training: { label: 'Scheduling Training', to: 'tr_sched' },
  training_scheduled: { label: 'Training Scheduled', to: 'tr_sched', booked: true },
  training_complete: { label: 'Training Complete', to: 'tr_done' },
  sprint_completed: { label: 'Project Completed', to: 'done' },
  custom_new: { label: 'New Project', to: 'cu_new' },
  custom_in_progress: { label: 'In Progress', to: 'cu_build' },
  custom_delivery_scheduled: { label: 'Delivery Call Scheduled', to: 'cu_ready' },
  // The pre-2026-06-16 lifecycle (New → Assigned → In Progress → Internal Review →
  // Delivered → Complete). Rows still carry these on production (#2 `complete`, #9
  // `internal_review`) and on staging's older copy. PR #1791 already read them onto the
  // custom track; the chain continues from there. Anything NOT listed here → Unsorted.
  new: { label: 'New', to: 'cu_new' },
  assigned: { label: 'Assigned', to: 'cu_new' },
  in_progress: { label: 'In Progress', to: 'cu_build' },
  internal_review: { label: 'Internal Review', to: 'cu_build' },
  delivered: { label: 'Delivered', to: 'cu_delivered' },
  complete: { label: 'Complete', to: 'done' },
};

/**
 * Statuses that are NOT open work: parked off the board, or the legacy Done marker.
 * "Open" = `completed_at IS NULL AND status NOT IN (these)` — a Done project is stamped
 * (`completed_at`, which `isProjectDone` reads for a stored row), so the pair covers every Done. Every count of
 * "open projects" (ops tiles, the client hub, the portal, capacity) uses this pair, so a
 * Canceled project never reads as open and never reaches a client as "completed".
 */
export const PROJECT_NOT_OPEN_STATUSES: readonly string[] = ['future', 'canceled', 'invalid', 'done'];

/** Old sprint-type key → new type. Anything not listed (tool names like "Zapier, LeadSimple") → null. */
export const LEGACY_PROJECT_TYPE_MAP: Record<string, ProjectTypeKey> = {
  full: 'full',
  documentation: 'documentation',
  build: 'implementation',
  training: 'training',
  custom: 'custom',
  doc_impl: 'doc_impl',
  implementation: 'implementation',
};

// ── Lookups ───────────────────────────────────────────────────────────────
export const PROJECT_TYPE_BY_KEY: Record<string, ProjectTypeDef> = Object.fromEntries(PROJECT_TYPES.map((t) => [t.key, t]));
export const PROJECT_PHASE_BY_ID: Record<string, ProjectPhaseDef> = Object.fromEntries(PROJECT_PHASES.map((p) => [p.id, p]));
export const PROJECT_OFF_BY_ID: Record<string, ProjectOffDef> = Object.fromEntries(PROJECT_OFF.map((o) => [o.id, o]));

/** Every stage key the model knows (the 4-call training maximum, sides included). */
export const PROJECT_STAGE_BY_KEY: Record<string, ProjectStageDef> = (() => {
  const out: Record<string, ProjectStageDef> = {};
  for (const ph of PROJECT_PHASES) {
    for (const s of ph.stages) out[s.key] = s;
    if (ph.side) out[ph.side.key] = ph.side;
  }
  return out;
})();

export const PROJECT_STAGE_KEYS: string[] = Object.keys(PROJECT_STAGE_BY_KEY);
export const PROJECT_OFF_KEYS: ProjectOffId[] = PROJECT_OFF.map((o) => o.id);

export function isProjectTypeKey(v: unknown): v is ProjectTypeKey {
  return typeof v === 'string' && (PROJECT_TYPE_KEYS as string[]).includes(v);
}
export function isProjectStageKey(v: unknown): v is string {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(PROJECT_STAGE_BY_KEY, v);
}
export function isProjectOffKey(v: unknown): v is ProjectOffId {
  return typeof v === 'string' && (PROJECT_OFF_KEYS as string[]).includes(v);
}
/** A value `projects.status` may be WRITTEN as (a stage or an off-board key). */
export function isWritableProjectStatus(v: unknown): v is string {
  return isProjectStageKey(v) || isProjectOffKey(v);
}
export function isSideStage(key: string | null | undefined): boolean {
  return !!(key && PROJECT_STAGE_BY_KEY[key]?.side);
}
export function phaseOfStage(key: string | null | undefined): ProjectPhaseId | null {
  return key && PROJECT_STAGE_BY_KEY[key] ? PROJECT_STAGE_BY_KEY[key].phase : null;
}

/** A phase's forward line for this project (Training follows the call count). */
export function phaseStages(phaseId: ProjectPhaseId, calls?: number | null): ProjectStageDef[] {
  if (phaseId === 'training') return trainingStages(calls);
  return PROJECT_PHASE_BY_ID[phaseId]?.stages ?? [];
}

/** Every forward stage a type walks, in order. */
export function typePath(type: ProjectTypeKey, calls?: number | null): ProjectStageDef[] {
  const t = PROJECT_TYPE_BY_KEY[type];
  if (!t) return [];
  return t.phases.flatMap((ph) => phaseStages(ph, calls));
}

/** Is `status` a stage this type walks (a side stage of one of its phases included)? */
export function stageInType(status: string | null | undefined, type: ProjectTypeKey, calls?: number | null): boolean {
  if (!status || !isProjectStageKey(status)) return false;
  const phase = phaseOfStage(status);
  const t = PROJECT_TYPE_BY_KEY[type];
  if (!t || !phase || !t.phases.includes(phase)) return false;
  if (phase === 'training') {
    // tr_call_N only exists while N < the call count.
    return phaseStages('training', calls).some((s) => s.key === status);
  }
  return true;
}

// ── Position ──────────────────────────────────────────────────────────────
export interface ProjectPositionInput {
  status: string | null | undefined;
  project_type: string | null | undefined;
  /** The type the board shows when `project_type` is null. */
  suggested_type?: ProjectTypeKey | null;
  training_call_count?: number | null;
  /**
   * The row's completion stamp. PRESENT (a date, or null) = a STORED row, and the
   * stamp decides Done. ABSENT (undefined) = a PREDICTION for a write — "would this
   * state be Done?" — which only a real type on its close stage answers yes.
   */
  completed_at?: string | null;
}

export type ProjectPositionKind = 'stage' | 'side' | 'off' | 'done' | 'unsorted';

export interface ProjectPosition {
  kind: ProjectPositionKind;
  /** The effective type: `project_type` when valid, else the suggestion, else 'custom'. */
  type: ProjectTypeKey;
  typed: boolean;
  /** The stage key (stage / side / done-at-close-stage), else null. */
  stage: string | null;
  phase: ProjectPhaseId | null;
  /** The off-board bucket (future / canceled / invalid / done), else null. */
  off: ProjectOffId | null;
}

/** The type a project is shown with: its own, else its suggestion, else Custom (the design's `pType`). */
export function effectiveType(p: ProjectPositionInput): ProjectTypeKey {
  if (isProjectTypeKey(p.project_type)) return p.project_type;
  if (p.suggested_type && isProjectTypeKey(p.suggested_type)) return p.suggested_type;
  return 'custom';
}

/**
 * Is the project Done?
 *   1. The legacy Done marker (`status === 'done'`) always is.
 *   2. A STORED row — the input carries `completed_at`, null included — is Done
 *      exactly when it is stamped. The stamp is the truth, whatever the stage says.
 *   3. Otherwise (`completed_at` undefined) this is a PREDICTION for a write: Done
 *      only when a REAL type (`project_type`) sits on its close stage. A suggested
 *      type never completes a project — an untyped project on its suggestion's close
 *      stage is open, on that stage, until someone gives it a type.
 * So: pass `completed_at` (even as null) when positioning a row you READ; leave it
 * out when asking whether a state you are about to WRITE would be Done. Parked
 * (Future / Canceled / Invalid) is never on a close stage, so never Done.
 */
export function isProjectDone(p: ProjectPositionInput): boolean {
  if (p.status === 'done') return true;
  if (p.completed_at !== undefined) return !!p.completed_at;
  return isProjectTypeKey(p.project_type) && !!p.status && p.status === CLOSE_STAGE[p.project_type];
}

/**
 * Where a project sits. Unsorted = open, but its stage is unknown OR not part of its
 * type's phases (README §4, open decision 1) — e.g. #119, typed Full on a Custom stage.
 */
export function projectPosition(p: ProjectPositionInput): ProjectPosition {
  const type = effectiveType(p);
  const typed = isProjectTypeKey(p.project_type);
  const status = p.status ?? null;
  if (status === 'future' || status === 'canceled' || status === 'invalid') {
    return { kind: 'off', type, typed, stage: null, phase: null, off: status };
  }
  if (isProjectDone(p)) {
    return { kind: 'done', type, typed, stage: status === 'done' ? null : status, phase: phaseOfStage(status), off: 'done' };
  }
  if (status && stageInType(status, type, p.training_call_count)) {
    return { kind: isSideStage(status) ? 'side' : 'stage', type, typed, stage: status, phase: phaseOfStage(status), off: null };
  }
  return { kind: 'unsorted', type, typed, stage: isProjectStageKey(status) ? status : null, phase: phaseOfStage(status), off: null };
}

/**
 * Where a project lands when its type changes (README §6.2): the stage stays when it
 * exists in the new type; otherwise the first stage of the first phase in the new type
 * the project has not already finished; if every phase is finished, the type's close
 * stage (→ Done).
 */
export function landingStage(
  p: { status: string | null | undefined; from_stage?: string | null; project_type: string | null | undefined; suggested_type?: ProjectTypeKey | null; training_call_count?: number | null },
  toType: ProjectTypeKey,
): { stage: string; keep: boolean; done: boolean } {
  const calls = p.training_call_count ?? null;
  const parked = p.status === 'future' || p.status === 'canceled' || p.status === 'invalid';
  const cur = parked ? (p.from_stage ?? null) : (p.status ?? null);
  const curPhase = phaseOfStage(cur);
  if (cur && curPhase && stageInType(cur, toType, calls)) return { stage: cur, keep: true, done: false };
  const fromPhases = PROJECT_TYPE_BY_KEY[effectiveType(p)]?.phases ?? [];
  const i = curPhase ? fromPhases.indexOf(curPhase) : -1;
  const finished = i > 0 ? fromPhases.slice(0, i) : [];
  const target = PROJECT_TYPE_BY_KEY[toType].phases.find((ph) => !finished.includes(ph));
  if (!target) return { stage: CLOSE_STAGE[toType], keep: false, done: true };
  return { stage: phaseStages(target, calls)[0].key, keep: false, done: false };
}

/** The first stage a brand-new project of this type starts on ("It starts on"). */
export function firstStageOfType(type: ProjectTypeKey, calls?: number | null): string {
  const t = PROJECT_TYPE_BY_KEY[type];
  return phaseStages(t.phases[0], calls)[0].key;
}

/**
 * Board drag (README §7.8): the smallest type containing the project's current phases +
 * the dropped phase; otherwise the dropped phase's single-phase type.
 */
export function typeForDrop(currentType: ProjectTypeKey, targetPhase: ProjectPhaseId): ProjectTypeKey {
  const want = new Set<ProjectPhaseId>([...PROJECT_TYPE_BY_KEY[currentType].phases, targetPhase]);
  const fits = PROJECT_TYPES
    .filter((t) => Array.from(want).every((ph) => t.phases.includes(ph)))
    .sort((a, b) => a.phases.length - b.phases.length);
  if (fits.length) return fits[0].key;
  const single = PROJECT_TYPES.find((t) => t.phases.length === 1 && t.phases[0] === targetPhase);
  return single ? single.key : 'custom';
}

/** A human label for any stored status (activity feed, CSV, MCP). Legacy keys keep their old label. */
export function projectStatusLabel(status: string | null | undefined): string {
  if (!status) return 'No stage';
  if (PROJECT_STAGE_BY_KEY[status]) return PROJECT_STAGE_BY_KEY[status].label;
  if (PROJECT_OFF_BY_ID[status]) return PROJECT_OFF_BY_ID[status].label;
  if (LEGACY_PROJECT_STAGE_MAP[status]) return LEGACY_PROJECT_STAGE_MAP[status].label;
  return status;
}

// ── Closing out (BULK-1, 2026-10-26) ──────────────────────────────────────

/**
 * PQR-010: the type that would close an UNCONFIRMED project. A project nobody
 * typed is shown with its suggestion ("Suggested · One-off / Custom"), and a
 * suggestion never completes it (`isProjectDone`), so one moved onto its
 * suggestion's close stage sits there, open, in every list of open work. This
 * names that suggestion so the record can ask once ("Is this a One-off / Custom
 * project?") and close it by confirming the type. Null for a typed, Done,
 * parked or mid-line project. Pass `completed_at` for a stored row; leave it
 * out to ask about a move that is about to be written.
 */
export function unconfirmedCloseType(p: ProjectPositionInput): ProjectTypeKey | null {
  if (isProjectTypeKey(p.project_type) || isProjectDone(p)) return null;
  const type = effectiveType(p);
  return !!p.status && p.status === CLOSE_STAGE[type] ? type : null;
}

/**
 * PQR-013: the stage a side stage can send the project back to. Leaving a side
 * stage returns the project to the stage it came from (`from_stage`), but only
 * a forward stage of its type can be returned to. A project that entered from
 * a stage outside its type (Unsorted: #119, a Full project on Custom Project
 * Ready for Delivery) has nowhere to go back to, so this is null and the person
 * picks the stage.
 */
export function sideReturnStage(from: string | null | undefined, type: ProjectTypeKey, calls?: number | null): string | null {
  if (!from || !isProjectStageKey(from) || isSideStage(from)) return null;
  return stageInType(from, type, calls) ? from : null;
}
